// "A receber": adiantamentos e saldos que os contratantes ainda devem,
// separados em atrasados, próximos 7 dias e depois, e os comprovantes que
// precisam ir pelo Correio. "Recebi" e "Postei" funcionam sem sinal e valem
// também em carga já acertada (não mexem na conta do acerto).
import {
  cargasEClientes, colaboradores, comprovanteEntregue, postarComprovante, receber, situacoesDeEnvio,
} from '../dados.js';
import { somarDias } from '../lib/despesas.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, placa as formatarPlaca, plural } from '../lib/formato.js';
import {
  comprovantes, itensAReceber, linhasDoPlacar, NOTAS_DO_PAGADOR, placarDosPagamentos, recebidosDesde, separarAReceber,
} from '../lib/receber.js';
import { nestaTela } from '../rotas.js';
import { hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, carregando, confirmar, dialogo, emblema, faixa, secao, vazio } from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';

// Totais para o cartão da tela inicial.
export async function resumoAReceber(hoje = hojeDaEmpresa()) {
  const { fretes, clientes } = await cargasEClientes();
  const grupos = separarAReceber(itensAReceber(fretes, clientes), hoje);
  const { aPostar } = comprovantes(fretes);
  return {
    ...grupos.totais,
    qtdAtrasados: grupos.atrasados.length,
    qtdSemana: grupos.semana.length,
    qtd: grupos.atrasados.length + grupos.semana.length + grupos.depois.length,
    aPostar: aPostar.length,
  };
}

const rotaDa = (f) => [f.origem_cidade && `${f.origem_cidade}${f.origem_uf ? `/${f.origem_uf}` : ''}`, f.destino_cidade && `${f.destino_cidade}${f.destino_uf ? `/${f.destino_uf}` : ''}`]
  .filter(Boolean).join(' → ');

export function telaAReceber() {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const hoje = hojeDaEmpresa();
    const [{ fretes, clientes, viagens, veiculos }, envio, pessoas] = await Promise.all([
      cargasEClientes(), situacoesDeEnvio('fretes'), colaboradores.listar()]);
    const gravavel = podeGravar();
    const itens = itensAReceber(fretes, clientes);
    const grupos = separarAReceber(itens, hoje);
    const recebidos = recebidosDesde(fretes, clientes, somarDias(hoje, -30));
    const { aPostar, postados } = comprovantes(fretes);
    const placaDa = (f) => {
      const v = viagens.find((x) => x.id === f.viagem_id);
      const c = v && veiculos.find((x) => x.id === v.veiculo_id);
      return c ? formatarPlaca(c.placa) : null;
    };
    const nomeDoCliente = (f) => clientes.find((c) => c.id === f.cliente_id)?.nome ?? 'Cliente não informado';

    const executar = async (acao, sucesso) => {
      try {
        await acao();
        avisar(sucesso);
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    };

    const recebi = async (item) => {
      const dia = await dialogoData({
        titulo: item.tipo === 'adiantamento' ? 'Recebi o adiantamento' : 'Recebi o saldo',
        detalhe: `${dinheiro(item.valor)} · ${nomeDoCliente(item.frete)} · carga de ${data(item.frete.data_carregamento)}`,
        rotulo: 'Recebido em',
        minimo: item.frete.data_carregamento,
        botao: 'Confirmar recebimento',
      });
      if (dia) executar(() => receber(item.frete.id, item.tipo, dia), 'Recebimento registrado.');
    };

    const desfazer = async (item) => {
      const ok = await confirmar({ titulo: 'Desfazer o recebimento?', texto: 'Ele volta para "A receber".', sim: 'Desfazer' });
      if (ok) executar(() => receber(item.frete.id, item.tipo, null), 'Voltou para "A receber".');
    };

    const postei = async (f) => {
      const r = await dialogoData({
        titulo: 'Postei o comprovante',
        detalhe: `${nomeDoCliente(f)} · carga de ${data(f.data_carregamento)}`,
        rotulo: 'Postado em',
        minimo: f.data_carregamento,
        comRastreio: true,
        botao: 'Confirmar',
      });
      if (r) executar(() => postarComprovante(f.id, { postado_em: r.dia, rastreio: r.rastreio }), 'Postagem registrada.');
    };

    const chegou = async (f) => {
      const ok = await confirmar({ titulo: 'O comprovante chegou?', texto: `${nomeDoCliente(f)} · carga de ${data(f.data_carregamento)}.`, sim: 'Chegou' });
      if (ok) executar(() => comprovanteEntregue(f.id), 'Comprovante entregue.');
    };

    const cartao = (item, { recebido = false } = {}) => {
      const f = item.frete;
      const atrasado = !recebido && item.vencimento < hoje;
      return h('div', { class: `cartao conta${atrasado ? ' vencida' : ''}` }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${dinheiro(item.valor)} · ${item.tipo === 'adiantamento' ? 'Adiantamento' : 'Saldo'} · ${nomeDoCliente(f)}`),
        h('span', { class: 'cartao-linha' }, recebido
          ? `Recebido em ${data(item.recebido_em)}`
          : `${atrasado ? 'Venceu em' : 'Vence em'} ${data(item.vencimento)}${item.tipo === 'saldo' ? ` (${f.data_descarga ? 'descarga' : 'carregamento'} + ${item.cliente?.prazo_saldo_dias ?? 30} dias)` : ''}`),
        h('span', { class: 'cartao-linha' }, [`Carga de ${data(f.data_carregamento)}`, placaDa(f), rotaDa(f)].filter(Boolean).join(' · ')),
        h('span', { class: 'cartao-emblemas' },
          recebido ? emblema('Recebido', 'ok') : atrasado ? emblema('Atrasado', 'perigo') : item.vencimento === hoje ? emblema('Vence hoje', 'alerta') : null,
          emblemaEnvio(envio.get(f.id))),
        h('div', { class: 'acoes-lado' },
          gravavel && (recebido
            ? h('button', { class: 'botao texto', type: 'button', onClick: () => desfazer(item) }, 'Desfazer')
            : h('button', { class: 'botao', type: 'button', onClick: () => recebi(item) }, 'Recebi')),
          h('a', { class: 'botao secundario', href: `#/cargas/${f.id}`, 'aria-label': 'Abrir a carga' }, 'Abrir'))));
    };

    const cartaoComprovante = (f, postado) => h('div', { class: 'cartao conta' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, `${nomeDoCliente(f)} · carga de ${data(f.data_carregamento)}`),
      h('span', { class: 'cartao-linha' }, [placaDa(f), rotaDa(f)].filter(Boolean).join(' · ')),
      postado && h('span', { class: 'cartao-linha' }, `Postado em ${data(f.comprovante_postado_em)}${f.comprovante_rastreio ? ` · rastreio ${f.comprovante_rastreio}` : ''}`),
      h('span', { class: 'cartao-emblemas' }, postado ? emblema('Postado', 'info') : emblema('Postar', 'alerta'), emblemaEnvio(envio.get(f.id))),
      h('div', { class: 'acoes-lado' },
        gravavel && h('button', { class: 'botao', type: 'button', onClick: () => (postado ? chegou(f) : postei(f)) }, postado ? 'Chegou' : 'Postei'),
        h('a', { class: 'botao secundario', href: `#/cargas/${f.id}`, 'aria-label': 'Abrir a carga' }, 'Abrir'))));

    // Placar de quem paga em dia: os clientes e, se houver, os agenciadores
    const placar = placarDosPagamentos(fretes, clientes, hoje);
    const placarAgenciadores = placarDosPagamentos(fretes, clientes, hoje, { por: 'agenciador_id' });
    const cartaoDoPlacar = (p, nome, href) => {
      const nota = NOTAS_DO_PAGADOR[p.nota];
      return h(href ? 'a' : 'div', { class: 'cartao placar', href }, h('span', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, nome),
        linhasDoPlacar(p).map((l) => h('span', { class: 'cartao-linha' }, l)),
        h('span', { class: 'cartao-emblemas' }, emblema(nota.texto, nota.tom))));
    };
    const nomeDaPessoa = (id) => {
      const x = pessoas.find((c) => c.id === id);
      return x?.apelido || x?.nome || 'Agenciador';
    };
    const secaoDoPlacar = placar.length > 0 && secao('Quem paga em dia', h('div', { class: 'lista' },
      h('p', { class: 'pequeno' }, 'Os saldos pagos nos últimos 12 meses e os atrasados agora, de cada cliente. Os que mais atrasam vêm primeiro.'),
      placar.map((p) => cartaoDoPlacar(p, clientes.find((c) => c.id === p.id)?.nome ?? 'Cliente', `#/clientes/${p.id}`)),
      placarAgenciadores.length > 0 && h('h3', { class: 'pequeno' }, 'Pelas cargas de cada agenciador'),
      placarAgenciadores.map((p) => cartaoDoPlacar(p, nomeDaPessoa(p.id), null))), { contador: String(placar.length) });

    const grupo = (titulo, lista, total) => lista.length > 0 && secao(titulo, h('div', { class: 'lista' }, lista.map((item) => cartao(item))), {
      contador: `${plural(lista.length, 'item', 'itens')} · ${dinheiro(total)}`,
    });

    trocar(corpo,
      itens.length === 0
        ? vazio({
          simbolo: 'check',
          titulo: 'Nada a receber',
          texto: 'Aqui aparecem os adiantamentos e os saldos de frete que os contratantes ainda não pagaram.',
        })
        : h('dl', { class: 'linha-dados resumo' },
          h('dt', null, 'Atrasados'), h('dd', { class: grupos.totais.atrasados > 0 ? 'perigo' : null }, dinheiro(grupos.totais.atrasados)),
          h('dt', null, 'Próximos 7 dias'), h('dd', null, dinheiro(grupos.totais.semana)),
          h('dt', null, 'Depois'), h('dd', null, dinheiro(grupos.totais.depois)),
          h('dt', null, 'Total a receber'), h('dd', null, dinheiro(grupos.totais.tudo))),
      grupo('Atrasados', grupos.atrasados, grupos.totais.atrasados),
      grupo('Próximos 7 dias', grupos.semana, grupos.totais.semana),
      grupo('Depois', grupos.depois, grupos.totais.depois),
      (aPostar.length > 0 || postados.length > 0) && secao('Comprovantes', h('div', { class: 'lista' },
        aPostar.map((f) => cartaoComprovante(f, false)),
        postados.map((f) => cartaoComprovante(f, true))), { contador: aPostar.length ? `${aPostar.length} para postar` : null }),
      h('p', { class: 'pequeno' }, 'O saldo vence na descarga (ou no carregamento, se a descarga não foi informada) mais o prazo do saldo do cliente. O prazo de cada cliente fica no cadastro dele.'),
      secaoDoPlacar,
      recebidos.length > 0 && h('details', { class: 'mais-detalhes' },
        h('summary', null, `Recebidos nos últimos 30 dias (${recebidos.length})`),
        h('div', { class: 'lista' }, recebidos.map((item) => cartao(item, { recebido: true })))),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'A receber', voltar: '/inicio', conteudo: [faixaDaConta(), corpo] });
}

// Diálogo com a data (e o rastreio, para o comprovante). Devolve a data, ou
// { dia, rastreio } com rastreio; null se cancelar.
function dialogoData({ titulo, detalhe, rotulo, minimo = null, comRastreio = false, botao }) {
  const hoje = hojeDaEmpresa();
  return dialogo((fechar) => {
    const quando = h('input', { type: 'date', id: 'receber-quando', value: hoje, max: hoje, min: minimo });
    const rastreio = h('input', { type: 'text', id: 'receber-rastreio', class: 'maiusculas', autocomplete: 'off', placeholder: 'Opcional' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, titulo),
      detalhe && h('p', null, detalhe),
      h('div', { class: 'campo' }, h('label', { for: 'receber-quando' }, rotulo), quando),
      comRastreio && h('div', { class: 'campo' }, h('label', { for: 'receber-rastreio' }, 'Código de rastreio'), rastreio),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            if (!quando.value) {
              erro.textContent = 'Informe a data.';
              return;
            }
            if (quando.value > hoje) {
              erro.textContent = 'A data não pode ser no futuro.';
              return;
            }
            if (minimo && quando.value < minimo) {
              erro.textContent = `A data não pode ser antes do carregamento (${data(minimo)}).`;
              return;
            }
            const texto = rastreio.value.replace(/\s+/g, '').toUpperCase() || null;
            fechar(comRastreio ? { dia: quando.value, rastreio: texto } : quando.value);
          },
        }, botao),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}
